// The Account page's two dialogs: setting your staff PIN at a venue (F-IAM-10, spec/07 §6.7), and the
// second factor's password-confirmed actions — new backup codes, or turning it off (F-IAM-09, §6.6). Both
// are centred dialogs (2026-10-07: the console has no slide-out panels).

import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, TextField, useToast } from '@clavian/ui-web';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { GuardedDialog, Notice } from '../ui';
import { STAFF_PIN_LENGTH, staffPinProblem } from './pin';
import { useDisableTwoFactor, useGenerateBackupCodes, useSetMyStaffPin } from './queries';

// -------------------------------------------------------------------------------------------------
// Staff PIN
// -------------------------------------------------------------------------------------------------

export function StaffPinDialog({
  organizationId,
  venue,
  onClose,
}: {
  readonly organizationId: string;
  /** The venue the PIN is for; `null` keeps the dialog closed. */
  readonly venue: { readonly id: string; readonly name: string } | null;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog
      open={venue !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      eyebrow={t('account.pin.eyebrow')}
      title={t('account.pin.sheetTitle', { venue: venue?.name ?? '' })}
      size="sm"
      dirty="auto"
    >
      {venue === null ? null : <StaffPinForm organizationId={organizationId} venueId={venue.id} onDone={onClose} />}
    </Dialog>
  );
}

function StaffPinForm({ organizationId, venueId, onDone }: { readonly organizationId: string; readonly venueId: string; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problems = useProblem();
  const { show } = useToast();
  const setPin = useSetMyStaffPin(organizationId);
  const [pin, setPinValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<{ field?: 'pin' | 'confirm'; message: string } | undefined>(undefined);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const problem = staffPinProblem(pin, confirm);
    if (problem !== undefined) {
      setError({ field: problem === 'mismatch' ? 'confirm' : 'pin', message: t(`account.pin.problem.${problem}`) });
      return;
    }
    setError(undefined);
    setPin.mutate(
      { venueId, pin },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('account.pin.saved') });
          onDone();
        },
        onError: (failure) => {
          const code = problemCode(failure);
          const message =
            code === PROBLEM.staffPinLocked
              ? t('account.pin.locked')
              : code === PROBLEM.sharedDevice
                ? t('account.pin.sharedDevice')
                : code === PROBLEM.validationFailed
                  ? t('account.pin.problem.run')
                  : problems.text(failure, { fallback: t('account.pin.failed') });
          setError({ message });
        },
      },
    );
  };

  const digitsOnly = (value: string) => value.replace(/\D/g, '').slice(0, STAFF_PIN_LENGTH);

  return (
    <form className="console-form" aria-label={t('account.pin.formLabel')} noValidate onSubmit={submit}>
      <Notice role="note">{t('account.pin.sheetHint')}</Notice>
      <TextField
        label={t('account.pin.new')}
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        maxLength={STAFF_PIN_LENGTH}
        required
        value={pin}
        onChange={(event) => setPinValue(digitsOnly(event.target.value))}
        {...(error?.field === 'pin' ? { error: error.message } : {})}
      />
      <TextField
        label={t('account.pin.confirm')}
        type="password"
        inputMode="numeric"
        autoComplete="new-password"
        maxLength={STAFF_PIN_LENGTH}
        required
        value={confirm}
        onChange={(event) => setConfirm(digitsOnly(event.target.value))}
        {...(error?.field === 'confirm' ? { error: error.message } : {})}
      />
      {error !== undefined && error.field === undefined ? <Notice tone="danger" role="alert" title={error.message} /> : null}
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={setPin.isPending}>
          {t('account.pin.save')}
        </Button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------------------------------
// The second factor
// -------------------------------------------------------------------------------------------------

export type TwoFactorAction = 'codes' | 'disable';

export function TwoFactorDialog({ action, onClose }: { readonly action: TwoFactorAction | null; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <GuardedDialog
      open={action !== null}
      onClose={onClose}
      eyebrow={t('account.twoFactor.title')}
      title={action === 'disable' ? t('account.twoFactor.disableTitle') : t('account.twoFactor.codesTitle')}
      size="sm"
      guard={{
        title: t('account.twoFactor.guardTitle'),
        body: t('account.twoFactor.guardBody'),
        keep: t('common.guard.keep'),
        discard: t('common.guard.discard'),
      }}
    >
      {(setGuarded) => (action === null ? null : <TwoFactorForm key={action} action={action} onDone={onClose} onGuardChange={setGuarded} />)}
    </GuardedDialog>
  );
}

function TwoFactorForm({ action, onDone, onGuardChange }: { readonly action: TwoFactorAction; readonly onDone: () => void; readonly onGuardChange?: (holding: boolean) => void }): ReactNode {
  const { t } = useTranslation('console');
  const problems = useProblem();
  const { show } = useToast();
  const codes = useGenerateBackupCodes();
  const disable = useDisableTwoFactor();
  const mutation = action === 'codes' ? codes : disable;
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [fresh, setFresh] = useState<readonly string[] | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  // Backup codes are shown once and never again, so arm the dialog's dismiss guard while they are on
  // screen: a stray Escape or scrim-click then asks before it throws them away.
  useEffect(() => {
    onGuardChange?.(fresh !== null);
  }, [fresh, onGuardChange]);

  const failure = (reason: unknown) => {
    const code = problemCode(reason);
    setError(
      code === 'INVALID_PASSWORD'
        ? t('account.twoFactor.wrongPassword')
        : code === 'SECOND_FACTOR_REQUIRED'
          ? t('account.twoFactor.needsSecondFactor')
          : problems.text(reason, { fallback: t('account.twoFactor.failed') }),
    );
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(undefined);
    const typed = password === '' ? undefined : password;
    if (action === 'codes') {
      codes.mutate(typed, { onSuccess: (result) => setFresh(result.backupCodes), onError: failure });
    } else {
      disable.mutate(typed, {
        onSuccess: () => {
          show({ tone: 'ok', title: t('account.twoFactor.disabled') });
          onDone();
        },
        onError: failure,
      });
    }
  };

  if (fresh !== null) {
    return (
      <div className="console-form">
        <Notice tone="warn" role="note" title={t('account.twoFactor.codesOnce')} />
        <ul className="console-account__codes" aria-label={t('account.twoFactor.codesLabel')}>
          {fresh.map((code) => (
            <li key={code} className="console-figure">
              {code}
            </li>
          ))}
        </ul>
        <div className="console-form__actions console-form__actions--end">
          <span role="status" className="console-muted">
            {copied ? t('account.twoFactor.copied') : ''}
          </span>
          <Button
            variant="secondary"
            onClick={() =>
              void navigator.clipboard?.writeText(fresh.join('\n')).then(
                () => setCopied(true),
                () => undefined,
              )
            }
          >
            {t('account.twoFactor.copy')}
          </Button>
          <Button variant="primary" onClick={onDone}>
            {t('common.done')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="console-form" aria-label={action === 'disable' ? t('account.twoFactor.disableTitle') : t('account.twoFactor.codesTitle')} noValidate onSubmit={submit}>
      <Notice tone={action === 'disable' ? 'warn' : 'info'} role="note">
        {action === 'disable' ? t('account.twoFactor.disableHint') : t('account.twoFactor.codesHint')}
      </Notice>
      <TextField
        label={t('account.twoFactor.password')}
        description={t('account.twoFactor.passwordHint')}
        type="password"
        autoComplete="current-password"
        showOptional
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      {error === undefined ? null : <Notice tone="danger" role="alert" title={error} />}
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant={action === 'disable' ? 'danger' : 'primary'} busy={mutation.isPending}>
          {action === 'disable' ? t('account.twoFactor.disableConfirm') : t('account.twoFactor.codesConfirm')}
        </Button>
      </div>
    </form>
  );
}
